// Functions for working with the computed RAD tree structure used for
// layout and rendering of the RAD.

import { defaultWidth, defaultHeight } from "./defaults.js";

/**
 * Creates a node object with default sizes.
 */
const createNode = (node, children = []) => {
  const isActivity = node.activity_id && !children.length;
  const size = isActivity
    ? { width: defaultWidth, height: defaultHeight }
    : {
        width: node.size?.width || defaultWidth,
        height: node.size?.height || defaultHeight,
      };

  return { node: { ...node, size }, children };
};

const getNodeById = (tree, id) => {
  let node = null;
  function traverse(tree) {
    if (tree.node.id === id) {
      node = tree.node;
    }
    tree.children.forEach((child) => traverse(child));
  }
  traverse(tree);
  return node;
};

const getNodeCollection = (tree, collection = []) => {
  let nodes = [];
  function traverse(tree) {
    if (collection.includes(tree.node.id)) {
      nodes.push(tree.node);
    }
    tree.children.forEach((child) => traverse(child));
  }
  traverse(tree);
  return nodes;
};

const getMainThread = (tree, thread_id) => {
  const threadNode = findChildByNodeId(tree, thread_id);
  if (!threadNode) return null;
  if (threadNode.node.class === "thread") return threadNode.node;
  return getMainThread(tree, threadNode.node.parent_id);
};

//

/**
 * Extracts user-defined positions
 */
const extractUserPositions = (radLayout) => {
  const rolePositions = radLayout.children.reduce((acc, role) => {
    acc[role.node.id] = role.node.position;
    return acc;
  }, {});

  const threadPositions = radLayout.children.flatMap((role) =>
    role.children.map((thread) => ({
      thread_id: thread.node.id,
      position: thread.node.position,
    }))
  );

  return { rolePositions, threadPositions };
};

/**
 * Gets the number of interaction nodes that precede a given node ID in document order.
 */
/**
 * Gets the number of interaction nodes that precede a given node ID within its main thread.
 */
const getPrecedingInteractionCount = (tree, targetId) => {
  // First find the target node and its main thread
  const targetNode = findChildByNodeId(tree, targetId);
  if (!targetNode) return 0;

  const mainThread = getMainThread(tree, targetNode.node.thread_id);
  if (!mainThread) return 0;

  // Get the main thread subtree
  const mainThreadSubtree = getSubtreeById(tree, mainThread.id);
  if (!mainThreadSubtree) return 0;

  let count = 0;
  let found = false;

  function traverse(subtree) {
    if (found) return;

    if (subtree.node.id === targetId) {
      found = true;
      return;
    }

    if (subtree.node.type === "interaction") {
      count++;
    }

    subtree.children.forEach((child) => traverse(child));
  }

  // Only traverse within the main thread
  traverse(mainThreadSubtree);
  return count;
};

/**
 * Extracts all child nodes of a given class and type.
 */
const getNodes = ({ tree, className, type = null, parent_id = null }) => {
  const isMatch = (subtree) =>
    subtree.node.class === className &&
    (type === null || subtree.node.type === type);
  const traverse = (subtree, parent_id, depth = 0) => {
    const currentNodeMatches =
      isMatch(subtree) && (!parent_id || subtree.node.parent_id === parent_id);

    const matchingChildren = subtree.children.flatMap((child) =>
      traverse(child, parent_id, depth + 1)
    );

    return currentNodeMatches
      ? [{ node: subtree.node, depth }, ...matchingChildren]
      : matchingChildren;
  };

  const nodesWithDepth = traverse(tree, parent_id);
  return nodesWithDepth.map((item, index) => ({
    ...item,
    index,
    precedingInteractions: getPrecedingInteractionCount(tree, item.node.id),
  }));
};

const getSubtreeById = (tree, id) => {
  let subtree = null;
  function traverse(tree) {
    if (tree.node.id === id) {
      subtree = tree;
    }
    tree.children.forEach((child) => traverse(child));
  }
  traverse(tree);
  return subtree;
};

const getChildrenById = (tree, id) => {
  const parent = getSubtreeById(tree, id);
  return parent ? parent.children : [];
};

const findChildByNodeId = (tree, id) => {
  if (tree.node.id === id) return tree;

  return (
    tree.children?.reduce((result, child) => {
      return result || findChildByNodeId(child, id);
    }, null) || null
  );
};

/**
 * Calculates the absolute position of a node within a tree structure.
 *
 * @param {Object} tree - The tree structure containing nodes.
 * @param {string} id - The ID of the node for which to calculate the absolute position.
 * @returns {Object} An object containing the absolute x and y coordinates of the node.
 * @returns {number} returns.x - The absolute x-coordinate of the node.
 * @returns {number} returns.y - The absolute y-coordinate of the node.
 */
const getAbsolutePosition = (tree, id) => {
  try {
    const child = findChildByNodeId(tree, id);
    if (!child) return { x: 0, y: 0 };

    const getPosition = (c) => {
      if (!c.node.parent_id) return c.node.position;

      const parent = findChildByNodeId(tree, c.node.parent_id);
      if (!parent) {
        return c.node.position;
      }
      const parentPosition = getPosition(parent);
      //console.log("Get Parent position", c.node, parentPosition);
      return {
        x: parentPosition.x + c.node.position.x,
        y: parentPosition.y + c.node.position.y,
      };
    };

    return getPosition(child);
  } catch (error) {
    console.error("Error in getAbsolutePosition:", error, { tree, id });
    return { x: 0, y: 0 };
  }
};

export {
  createNode,
  getNodeById,
  extractUserPositions,
  getNodes,
  getNodeCollection,
  findChildByNodeId,
  getAbsolutePosition,
  getChildrenById,
  getSubtreeById,
  getMainThread,
  getPrecedingInteractionCount,
};
